import APP_ASSETS from "@/assets";
import { LoadingSpinner } from "@/components/";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ButtonSize } from "@/components/ui/button/button.type";
import { Button } from "@/components/ui/button/button";
import { LocalStorageKeys, STEPS_TO_GET_STARTED } from "@/constants";
import { useMerchant, useSmartWallet } from "@/contexts";
import { copyToClipboard, maskAddress } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConnectWalletCta } from "@/components/connect-wallet/connect-wallet";
import { LogOut } from "lucide-react";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import { trackAmplitudeEvent } from "@/utils/amplitude";

const OnboardingOne = ({
  setGotStarted,
}: {
  setGotStarted: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { isLoggedIn } = useSmartWallet();

  useEffect(() => {
    if (isLoggedIn) {
      trackAmplitudeEvent("ONBOARDING_WALLET_CONNECTED");
      setGotStarted(true);
    }
  }, [isLoggedIn]);

  return (
    <main className="relative flex h-full max-h-[1200px] w-full max-w-[500px] flex-col bg-bg-tertiary">
      <section className="bg-onboarding-gradient flex h-[65%] w-full items-center justify-center">
        <div className="shadow-blackAllSides flex h-40 w-40 items-center justify-center rounded-full border-4 border-primary bg-bg-tertiary">
          <img
            className="h-24 w-24 object-contain"
            src={APP_ASSETS.ICONS.P2PX_LOGO}
            alt={APP_ASSETS.ICONS.P2PX_LOGO}
          />
        </div>
      </section>
      <section className="flex h-[25%] w-full flex-col items-start justify-start gap-4 px-4 py-8">
        <p className="text-2xl font-bold text-text-primary">
          Become a P2P.me Merchant
        </p>
        <p className="text-sm text-text-light">
          Earn a 1% profit on every transaction. For example, fulfill $5,000
          USDC in buy and sell orders to earn $50 USDC.
        </p>
      </section>
      <section className="flex h-[10%] w-full items-center justify-end p-8">
        <div className="w-42 mb-12">
          <ConnectWalletCta size={ButtonSize.small} />
        </div>
      </section>
    </main>
  );
};

const RedirectButtonFromOnboarding = ({
  isApproved,
}: {
  isApproved: boolean;
}) => {
  const navigate = useNavigate();
  if (!isApproved) {
    return (
      <section className="flex h-full w-full items-center justify-center px-8 py-4">
        <Button
          label="Go to P2P.me Merchants Telegram group"
          onClick={() => {
            trackAmplitudeEvent("MERCHANT_TELEGRAM_GROUP_CLICKED_ONBOARDING");
            window.open("https://t.me/merchants_p2p", "_blank");
          }}
          widthFull
          size={ButtonSize.small}
        />
      </section>
    );
  }

  return (
    <section className="flex h-full w-full items-center justify-center px-8 py-4">
      <Button
        label="Register by staking USDC"
        onClick={() => {
          trackAmplitudeEvent("MERCHANT_STAKE_CLICKED_ONBOARDING");
          navigate("/stake");
        }}
        size={ButtonSize.small}
        widthFull
      />
    </section>
  );
};

const OnboardingTwo = () => {
  const { smartWalletAddress, logout } = useSmartWallet();
  const { hasFetched, isLoading, merchantConfig, merchantDetails } =
    useMerchant();
  const privacyPolicyAccepted = localStorage.getItem(
    LocalStorageKeys?.PRIVACY_POLICY_ACCEPTED,
  );

  return (
    <main className="relative flex h-full max-h-[1200px] w-full max-w-[500px] flex-col items-center gap-6 bg-bg-tertiary">
      <div className="no-scrollbar flex h-full w-full flex-col items-center overflow-y-scroll py-2">
        {/* APP */}
        <section className="flex w-full flex-col items-center justify-center gap-2 p-4">
          <div className="shadow-primaryAllSides flex h-12 w-12 items-center justify-center rounded-full border-[1px] border-primary bg-bg-tertiary">
            <img
              className="ml-1 h-6 w-6 object-contain"
              src={APP_ASSETS.ICONS.MAIN_P2PME}
              alt={APP_ASSETS.ICONS.P2PX_LOGO}
            />
          </div>
          <p className="font-satoshiBlack text-2xl font-bold text-text-primary">
            P2P.me
          </p>
          <p className="text-sm text-gray-300">
            An app for merchants on P2P.me protocol
          </p>
        </section>
        {/* APP */}
        {/* PROFILE */}
        <section className="flex w-[85%] items-center justify-center gap-4 rounded-md border border-primary p-4">
          <Avatar>
            <AvatarImage src={APP_ASSETS.IMAGES.GOKU} />
            <AvatarFallback>GOKU</AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start justify-center gap-2">
            <div className="row flex">
              <p className="text-left text-sm text-text-primary">
                Your P2P.me Wallet Address (Base)
              </p>
              <LogOut
                onClick={() => {
                  trackAmplitudeEvent("MERCHANT_LOGGED_OUT_ONBOARDING");
                  logout();
                }}
                className="mx-2 h-[22px] w-[22px] text-text-primary hover:cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-2">
              <p className="text-sm text-text-light">
                {maskAddress((smartWalletAddress ?? "") as string, 10)}
              </p>
              <img
                onClick={() => copyToClipboard(smartWalletAddress as string)}
                className="cursor-pointer"
                src={APP_ASSETS.ICONS.PASTE}
                alt={APP_ASSETS.ICONS.PASTE}
              />
            </div>
          </div>
        </section>
        {/* PROFILE */}
        {isLoading || !hasFetched || !merchantConfig || !merchantDetails ? (
          <div className="flex h-full w-[80%] items-center justify-center px-8 py-4">
            <LoadingSpinner size={48} />
          </div>
        ) : (
          <>
            {/* STEPS TO GET STARTED */}
            {!merchantDetails.isApproved ? (
              <section className="flex w-full flex-col items-start justify-center px-8 py-4">
                <p className="font-bold text-text-primary">
                  Get started in 3 steps
                </p>
                <div className="flex w-full flex-col items-start justify-center gap-6 py-4">
                  {STEPS_TO_GET_STARTED.map((step, idx) => (
                    <div
                      key={idx}
                      className="flex w-full items-start justify-between gap-4">
                      <p className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-primary font-bold">
                        {idx + 1}
                      </p>
                      <p className="w-[85%] font-bold text-text-light">
                        {step}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
            {/* STEPS TO GET STARTED */}
            {/* GO TO TELEGRAM - TEMPORARILY GOES HOME */}
            <RedirectButtonFromOnboarding
              isApproved={merchantDetails.isApproved as boolean}
            />
            {/* GO TO TELEGRAM */}
          </>
        )}
      </div>

      {!privacyPolicyAccepted && <PrivacyPolicy onClose={() => {}} />}
    </main>
  );
};

const Onboarding = () => {
  const [gotStarted, setGotStarted] = useState(false);

  if (gotStarted) {
    return <OnboardingTwo />;
  } else {
    return <OnboardingOne setGotStarted={setGotStarted} />;
  }
};

export default Onboarding;
